import { EChartsOption } from 'echarts';
import { DataInputConfig } from '../../../shared/model';
import { ExperimentDefinition, EXPERIMENT_MAP, ProbabilityParams, TrialResult } from './probability.model';

type RngFn = () => number;

/** Mulberry32 seeded PRNG — deterministic, self-contained */
export function createRng(seed: string): RngFn {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  let s = h | 0;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Run a single experiment with the given trial size */
export function runExperiment(experiment: ExperimentDefinition, trialSize: number, rng: RngFn): TrialResult {
  const outcomeCounts: Record<string, number> = {};
  for (const outcome of experiment.outcomes) {
    outcomeCounts[outcome] = 0;
  }

  for (let i = 0; i < trialSize; i++) {
    const index = Math.floor(rng() * experiment.outcomes.length);
    outcomeCounts[experiment.outcomes[index]]++;
  }

  const targetCount = outcomeCounts[experiment.targetOutcome] ?? 0;
  return {
    trialSize,
    outcomeCounts,
    relativeFrequency: targetCount / trialSize,
  };
}

/** Run experiments for all trial sizes (each independent, sorted ascending) */
export function runAllExperiments(params: ProbabilityParams): TrialResult[] {
  const experiment = EXPERIMENT_MAP[params.experiment];
  const target = params.targetOutcome ?? experiment.targetOutcome;
  const effectiveExperiment =
    target !== experiment.targetOutcome ? { ...experiment, targetOutcome: target } : experiment;
  const rng = createRng(params.seed ?? 'default');
  const sorted = [...params.trialSizes].sort((a, b) => a - b);
  return sorted.map((size) => runExperiment(effectiveExperiment, size, rng));
}

/** Build AG Grid DataInputConfig from experiment results */
export function buildDataInputConfig(experiment: ExperimentDefinition, results: TrialResult[]): DataInputConfig {
  const columns = [
    { id: 'trials', header: 'Trials', colType: 'number' as const, width: 80 },
    ...experiment.outcomes.map((outcome) => ({
      id: outcome.toLowerCase().replace(/\s+/g, '_'),
      header: outcome,
      colType: 'number' as const,
      width: 80,
    })),
    {
      id: 'pTarget',
      header: `P(${experiment.targetOutcome})`,
      colType: 'number' as const,
      width: 100,
    },
  ];

  const rows = results.map((r) => {
    const row: Record<string, number | string | boolean | null> = { trials: r.trialSize };
    for (const outcome of experiment.outcomes) {
      row[outcome.toLowerCase().replace(/\s+/g, '_')] = r.outcomeCounts[outcome];
    }
    row['pTarget'] = parseFloat(r.relativeFrequency.toFixed(3));
    return row;
  });

  return {
    columns,
    rows,
    editableHeaders: false,
    allowAddColumns: false,
    allowAddRows: false,
  };
}

/** Build ECharts options matching the existing probability chart styling */
export function buildChartOptions(
  experiment: ExperimentDefinition,
  results: TrialResult[],
  yAxisRange?: [number, number],
): EChartsOption {
  const trialLabels = results.map((r) => String(r.trialSize));
  const experimentalData = results.map((r) => parseFloat(r.relativeFrequency.toFixed(4)));
  const theoreticalData = results.map(() => experiment.theoreticalProbability);
  const pLabel = `P = ${
    Number.isInteger(experiment.theoreticalProbability)
      ? String(experiment.theoreticalProbability)
      : parseFloat(experiment.theoreticalProbability.toFixed(4))
  }`;

  const [yMin, yMax] = yAxisRange ?? [0, 1];
  const interval = parseFloat(((yMax - yMin) / 4).toFixed(4));

  return {
    animation: false,
    grid: {
      left: 8,
      right: 16,
      top: 24,
      bottom: 8,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      name: 'Number of trials',
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      data: trialLabels,
      axisLabel: { fontSize: 12, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#d1d5db' } },
    },
    yAxis: {
      type: 'value',
      min: yMin,
      max: yMax,
      interval,
      name: `Relative frequency of ${experiment.targetOutcome.toLowerCase()}`,
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisLabel: { fontSize: 12, color: '#6b7280' },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLine: { lineStyle: { color: '#d1d5db' } },
    },
    series: [
      {
        type: 'line',
        data: experimentalData,
        smooth: false,
        lineStyle: { color: '#9ca3af', width: 2 },
        itemStyle: { color: '#9ca3af' },
        symbolSize: 8,
      },
      {
        type: 'line',
        data: theoreticalData,
        lineStyle: { color: '#6366f1', width: 1.5, type: 'dashed' },
        itemStyle: { color: '#6366f1' },
        symbolSize: 0,
        markPoint: {
          data: [
            {
              name: 'theoretical',
              coord: [trialLabels[trialLabels.length - 1], experiment.theoreticalProbability],
              symbolSize: 0,
              label: {
                show: true,
                formatter: pLabel,
                position: 'right',
                fontSize: 12,
                fontWeight: 'bold',
                color: '#6366f1',
              },
            },
          ],
        },
      },
    ],
    tooltip: { show: false },
  };
}
